import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { ConnectWalletModal } from './modals/ConnectWalletModal'

/**
 * LocalStorage key for storing terms acceptance.
 * Using a versioned key allows us to re-prompt users when terms change.
 */
const TERMS_ACCEPTED_KEY = 'ctopools_terms_accepted_v1'

/**
 * Interface for TermsContext value.
 * Provides methods to check and update terms acceptance status,
 * and to open the unified connect-wallet modal.
 */
interface TermsContextValue {
  /** Whether the user has accepted the current version of terms */
  hasAcceptedTerms: boolean
  /** Check if user should be prompted for terms (for wallet connection) */
  shouldPromptTerms: boolean
  /** Function to mark terms as accepted (persists to localStorage) */
  acceptTerms: () => void
  /** Function to revoke terms acceptance (for testing/settings) */
  revokeTermsAcceptance: () => void
  /** Opens the unified Connect Wallet modal (ToS → Wallet selection) */
  openConnectModal: () => void
}

/**
 * Default context value.
 * Used when context is accessed outside of provider.
 */
const defaultContextValue: TermsContextValue = {
  hasAcceptedTerms: false,
  shouldPromptTerms: true,
  acceptTerms: () => {},
  revokeTermsAcceptance: () => {},
  openConnectModal: () => {},
}

/**
 * Terms Context.
 * Provides terms acceptance state and methods throughout the app.
 */
const TermsContext = createContext<TermsContextValue>(defaultContextValue)

/**
 * Props for TermsProvider component
 */
interface TermsProviderProps {
  children: ReactNode
}

/**
 * TermsProvider Component
 *
 * Wraps the application to provide terms acceptance state management
 * and the unified wallet-connection modal.
 *
 * This version renders a single ConnectWalletModal that combines:
 * 1. Terms of Service acceptance
 * 2. Wallet selection
 *
 * The UX flow:
 * 1. User clicks "Select Wallet" button
 * 2. Unified modal opens — if ToS not accepted, shows ToS step first
 * 3. After accepting ToS, modal transitions to wallet selection step
 * 4. User picks a wallet and connects (single popup, no double modal)
 *
 * If ToS is already accepted, clicking "Select Wallet" skips straight
 * to the wallet selection step.
 */
export function TermsProvider({ children }: TermsProviderProps): JSX.Element {
  // State for tracking terms acceptance
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false)
  // State for controlling the unified modal visibility
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  // Flag to track if we've done initial localStorage check
  const [_isInitialized, setIsInitialized] = useState<boolean>(false)

  /**
   * Load terms acceptance status from localStorage on mount.
   * Checks if user has previously accepted the current version of terms.
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(TERMS_ACCEPTED_KEY)
      console.log('[TermsProvider] Checking localStorage for TOS acceptance:', stored)

      if (stored) {
        const data = JSON.parse(stored)
        // Check if stored acceptance is valid
        if (data.accepted && data.timestamp) {
          console.log('[TermsProvider] TOS already accepted on:', data.timestamp)
          setHasAcceptedTerms(true)
        }
      } else {
        console.log('[TermsProvider] TOS not yet accepted — will show modal on wallet connect')
      }
    } catch (error) {
      console.error('Error loading terms acceptance status:', error)
    }
    setIsInitialized(true)
  }, [])

  /**
   * Calculate whether we should prompt for terms.
   * Used by components to determine if they should show a terms prompt.
   */
  const shouldPromptTerms = !hasAcceptedTerms

  /**
   * Accept terms and persist to localStorage.
   * Records the acceptance timestamp for audit purposes.
   * Called by the ConnectWalletModal when user checks the box and clicks "Accept & Connect".
   */
  const acceptTerms = useCallback(() => {
    try {
      const data = {
        accepted: true,
        timestamp: new Date().toISOString(),
        version: 'v1',
      }
      localStorage.setItem(TERMS_ACCEPTED_KEY, JSON.stringify(data))
      setHasAcceptedTerms(true)
      console.log('[TermsProvider] TOS accepted and persisted')
    } catch (error) {
      console.error('Error saving terms acceptance:', error)
    }
  }, [])

  /**
   * Revoke terms acceptance.
   * Removes acceptance from localStorage (for testing or user settings).
   */
  const revokeTermsAcceptance = useCallback(() => {
    try {
      localStorage.removeItem(TERMS_ACCEPTED_KEY)
      setHasAcceptedTerms(false)
      console.log('[TermsProvider] TOS acceptance revoked')
    } catch (error) {
      console.error('Error revoking terms acceptance:', error)
    }
  }, [])

  /**
   * Open the unified Connect Wallet modal.
   * The modal will determine its initial step based on hasAcceptedTerms:
   * - If terms accepted → skip straight to wallet selection
   * - If terms not accepted → show ToS step first
   */
  const openConnectModal = useCallback(() => {
    console.log('[TermsProvider] Opening unified connect modal', {
      hasAcceptedTerms,
    })
    setIsModalOpen(true)
  }, [hasAcceptedTerms])

  /**
   * Close the unified modal without performing any action.
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const contextValue: TermsContextValue = {
    hasAcceptedTerms,
    shouldPromptTerms,
    acceptTerms,
    revokeTermsAcceptance,
    openConnectModal,
  }

  return (
    <TermsContext.Provider value={contextValue}>
      {children}
      <ConnectWalletModal
        isOpen={isModalOpen}
        onAcceptTerms={acceptTerms}
        onClose={handleCloseModal}
        hasAcceptedTerms={hasAcceptedTerms}
      />
    </TermsContext.Provider>
  )
}

/**
 * useTerms Hook
 *
 * Custom hook for accessing terms acceptance state and methods.
 * Must be used within a TermsProvider.
 *
 * @returns TermsContextValue with acceptance state, control methods, and openConnectModal
 * @throws Error if used outside of TermsProvider
 *
 * @example
 * ```tsx
 * const { hasAcceptedTerms, openConnectModal } = useTerms()
 *
 * const handleConnect = () => {
 *   // Opens unified modal — handles ToS + wallet selection in one popup
 *   openConnectModal()
 * }
 * ```
 */
export function useTerms(): TermsContextValue {
  const context = useContext(TermsContext)
  if (!context) {
    throw new Error('useTerms must be used within a TermsProvider')
  }
  return context
}

export default TermsProvider
